import type { ComponentType } from 'react';
import {
  LayoutDashboard,
  Sparkles,
  Share2,
  Palette,
  Video,
  Globe2,
  Layout,
  PenSquare,
  Link2,
  Megaphone,
  Bot,
  Users,
  Workflow,
  Radio,
  Swords,
  LayoutGrid,
  CreditCard,
  Settings,
} from 'lucide-react';

// Fonte única da navegação: a barra lateral tem UM item por assunto e as telas irmãs
// de cada assunto viram abas no topo da página (SectionTabs). Para mudar o menu, mexa só aqui.
//
// Decisão de produto: Rastreamento do site (/audiences, /rastreamento) NÃO entra no menu —
// o acesso fica em botões nas telas relacionadas. Relatórios (/reports) não existe mais
// como tela (redireciona para o Início, onde os números ficam), então não aparece aqui.
type Icon = ComponentType<{ className?: string }>;

export type NavModule = 'contacts' | 'liveAutomation';

export interface SectionTab {
  href: string;
  key: string;
  fallback: string; // texto se a tradução não existir
  icon: Icon;
  match?: string[]; // outras telas de LISTA que acendem esta aba e mostram as abas
  prefix?: boolean; // qualquer caminho abaixo de href também conta como esta aba (ex.: /blog-seo/:tab)
  module?: NavModule; // só aparece com o módulo ligado (Configurações → Módulos)
}

export interface SectionGroup {
  id: string;
  key: string;
  fallback: string;
  icon: Icon;
  tour?: string; // data-tour do item na barra lateral
  tabs: SectionTab[];
  account?: boolean; // vai para o rodapé da barra (Conta)
  desc?: string; // linha curta embaixo do nome na barra lateral
  highlight?: boolean; // item em destaque na barra lateral
  tone?: Tone; // cor do assunto (ícone e destaque na barra lateral)
}

// Cores por assunto na barra lateral: ícone colorido parado e bloco em degradê quando ativo
export type Tone = 'brand' | 'pink' | 'sky' | 'amber' | 'emerald' | 'violet' | 'rose' | 'slate';
export const TONES: Record<Tone, { idle: string; active: string; bar: string }> = {
  brand: { idle: 'bg-primary-500/10 text-primary-400', active: 'bg-gradient-to-br from-primary-500 to-purple-600 text-white shadow-lg shadow-primary-900/40', bar: 'from-primary-400 to-purple-500' },
  pink: { idle: 'bg-pink-500/10 text-pink-400', active: 'bg-gradient-to-br from-pink-500 to-orange-500 text-white shadow-lg shadow-pink-900/40', bar: 'from-pink-400 to-orange-400' },
  sky: { idle: 'bg-sky-500/10 text-sky-400', active: 'bg-gradient-to-br from-sky-500 to-blue-600 text-white shadow-lg shadow-sky-900/40', bar: 'from-sky-400 to-blue-500' },
  amber: { idle: 'bg-amber-500/10 text-amber-400', active: 'bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-900/40', bar: 'from-amber-400 to-orange-500' },
  emerald: { idle: 'bg-emerald-500/10 text-emerald-400', active: 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-900/40', bar: 'from-emerald-400 to-teal-500' },
  violet: { idle: 'bg-violet-500/10 text-violet-400', active: 'bg-gradient-to-br from-violet-500 to-indigo-600 text-white shadow-lg shadow-violet-900/40', bar: 'from-violet-400 to-indigo-500' },
  rose: { idle: 'bg-rose-500/10 text-rose-400', active: 'bg-gradient-to-br from-rose-500 to-red-600 text-white shadow-lg shadow-rose-900/40', bar: 'from-rose-400 to-red-500' },
  slate: { idle: 'bg-gray-800/60 text-gray-400', active: 'bg-gradient-to-br from-gray-600 to-gray-700 text-white shadow-lg shadow-black/30', bar: 'from-gray-300 to-gray-500' },
};

export const SECTION_GROUPS: SectionGroup[] = [
  {
    id: 'home', key: 'nav.sections.home', fallback: 'Início', icon: LayoutDashboard, tone: 'brand', desc: 'Resumo dos resultados', tour: 'nav-dashboard',
    tabs: [{ href: '/dashboard', key: 'nav.sections.home', fallback: 'Início', icon: LayoutDashboard }],
  },
  {
    id: 'create', key: 'nav.sections.create', fallback: 'Criar conteúdo', icon: Sparkles, tone: 'pink', desc: 'Posts, imagens e vídeos', tour: 'nav-social-media',
    tabs: [
      {
        href: '/content', key: 'nav.sections.social', fallback: 'Redes sociais', icon: Share2,
        match: ['/content/all', '/content/calendar', '/content/analytics', '/content/integrations', '/content/generate', '/content/old'],
      },
      { href: '/image-studio', key: 'nav.sections.images', fallback: 'Imagens', icon: Palette },
      { href: '/video-studio', key: 'nav.sections.videos', fallback: 'Vídeos', icon: Video },
    ],
  },
  {
    // Blog e SEO tem item próprio e em destaque: é a parte de SEO de verdade (aparecer no Google)
    id: 'seo', key: 'nav.sections.seo', fallback: 'Blog e SEO', icon: PenSquare, tone: 'emerald', desc: 'Apareça no Google', highlight: true, tour: 'nav-blog-seo',
    tabs: [{ href: '/blog-seo', key: 'nav.sections.seo', fallback: 'Blog e SEO', icon: PenSquare, prefix: true }],
  },
  {
    id: 'site', key: 'nav.sections.pagesLinks', fallback: 'Páginas e links', icon: Globe2, tone: 'sky', desc: 'Páginas de venda e links', tour: 'nav-landing-pages',
    tabs: [
      { href: '/landing-pages', key: 'nav.sections.landingPages', fallback: 'Páginas de venda', icon: Layout },
      { href: '/short-links', key: 'nav.sections.shortLinks', fallback: 'Links curtos', icon: Link2 },
    ],
  },
  {
    id: 'ads', key: 'nav.sections.ads', fallback: 'Anúncios', icon: Megaphone, tone: 'amber', desc: 'Google, Meta e mais', tour: 'nav-campaigns',
    tabs: [
      { href: '/campaigns', key: 'nav.sections.campaigns', fallback: 'Campanhas', icon: Megaphone },
      { href: '/campaigns/autopilot', key: 'nav.sections.trafficManager', fallback: 'Gestor de tráfego', icon: Bot },
    ],
  },
  {
    id: 'contacts', key: 'nav.sections.contacts', fallback: 'Contatos', icon: Users, tone: 'emerald', desc: 'Quem chegou até você', tour: 'nav-contacts',
    tabs: [{ href: '/contacts', key: 'nav.sections.contacts', fallback: 'Contatos', icon: Users, module: 'contacts' }],
  },
  {
    id: 'automations', key: 'nav.sections.automations', fallback: 'Automações', icon: Workflow, tone: 'violet', desc: 'Tarefas que rodam sozinhas', tour: 'nav-workflows',
    tabs: [
      { href: '/workflows', key: 'nav.sections.flows', fallback: 'Fluxos', icon: Workflow },
      { href: '/live-automation', key: 'nav.sections.live', fallback: 'Ao vivo', icon: Radio, module: 'liveAutomation' },
    ],
  },
  {
    id: 'competitors', key: 'nav.sections.competitors', fallback: 'Concorrentes', icon: Swords, tone: 'rose', desc: 'Compare e ganhe posição', tour: 'nav-competitors',
    tabs: [
      { href: '/competitors', key: 'nav.sections.overview', fallback: 'Visão geral', icon: LayoutGrid },
      // Ferramentas avulsas (palavras-chave, posição no Google, auditoria, backlinks, explorar site)
      // ficam fora do menu: sem o provedor de dados (DataForSEO) os números são só estimativas e
      // não batem entre si. As rotas continuam existindo para quando o provedor for configurado.
    ],
  },
  {
    id: 'plan', key: 'nav.sections.plan', fallback: 'Plano', icon: CreditCard, tone: 'slate', account: true, tour: 'nav-subscription',
    tabs: [{ href: '/subscription', key: 'nav.sections.plan', fallback: 'Plano', icon: CreditCard }],
  },
  {
    id: 'settings', key: 'nav.sections.settings', fallback: 'Configurações', icon: Settings, tone: 'slate', account: true, tour: 'nav-settings',
    tabs: [{ href: '/settings', key: 'nav.sections.settings', fallback: 'Configurações', icon: Settings }],
  },
];

export type ModuleFlags = Partial<Record<NavModule, boolean>> | null | undefined;

const under = (pathname: string, href: string) => pathname === href || pathname.startsWith(href + '/');

// Abas que valem para esta conta (módulos desligados somem)
export const visibleTabs = (group: SectionGroup, modules: ModuleFlags): SectionTab[] =>
  group.tabs.filter((tab) => !tab.module || !!modules?.[tab.module]);

// Caminhos que acendem um grupo inteiro (todas as abas e as telas de dentro delas)
export const groupPaths = (tabs: SectionTab[]) => tabs.flatMap((tab) => [tab.href, ...(tab.match || [])]);

export const isGroupActive = (tabs: SectionTab[], pathname: string) =>
  groupPaths(tabs).some((href) => under(pathname, href));

// Aba exatamente desta tela (só telas de lista; telas abertas têm cabeçalho próprio)
export const isTabExact = (tab: SectionTab, pathname: string) =>
  pathname === tab.href || (tab.match || []).includes(pathname) || (!!tab.prefix && under(pathname, tab.href));

export const findGroup = (pathname: string, modules: ModuleFlags) => {
  // grupo cuja aba bate exatamente vem primeiro (ex.: /competitors/site-audit é aba, não "dentro de" /competitors)
  const exact = SECTION_GROUPS.find((g) => visibleTabs(g, modules).some((tab) => isTabExact(tab, pathname)));
  return exact || SECTION_GROUPS.find((g) => isGroupActive(visibleTabs(g, modules), pathname));
};
